import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Clock, User, Envelope, Phone, Buildings, ChatCircle, Check, CaretDown, CurrencyDollar } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EVENT_OPTIONS = [
  { value: 'kickoff', label: 'June 11th - 14th World Soccer Kickoff Festival & Party' },
  { value: 'finale', label: 'July 18 & 19 World Soccer Finale Festival & Party' },
  { value: 'both', label: 'Kickoff & Finale World Soccer Party' },
  { value: 'vip', label: 'VIP Exclusive' },
  { value: 'other', label: 'Other' },
];

const COUNTRY_CODES = [
  { value: '+1', label: 'US/CA +1' },
  { value: '+52', label: 'MX +52' },
  { value: '+44', label: 'UK +44' },
  { value: '+49', label: 'DE +49' },
  { value: '+33', label: 'FR +33' },
  { value: '+34', label: 'ES +34' },
  { value: '+55', label: 'BR +55' },
  { value: '+61', label: 'AU +61' },
  { value: '+91', label: 'IN +91' },
];

const BUDGET_RANGES = [
  { value: 'under-2500', label: 'Under $2,500' },
  { value: '2500-5000', label: '$2,500 - $5,000' },
  { value: '5000-10000', label: '$5,000 - $10,000' },
  { value: '10000-25000', label: '$10,000 - $25,000' },
  { value: '25000-plus', label: '$25,000+' },
];

const TIME_SLOTS = (() => {
  const slots = [];
  for (let h = 8; h <= 21; h++) {
    for (let m = 0; m < 60; m += 30) {
      if (h === 21 && m > 0) break;
      const hour12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
      const ampm = h >= 12 ? 'PM' : 'AM';
      const minStr = m === 0 ? '00' : '30';
      slots.push({ value: `${h.toString().padStart(2, '0')}:${minStr}`, label: `${hour12}:${minStr} ${ampm} EST` });
    }
  }
  return slots;
})();

export const SponsorSection = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    phone_code: '+1',
    preferred_date: '',
    preferred_time: '',
    event_dates: [],
    budget_range: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [eventsOpen, setEventsOpen] = useState(false);
  const [showPremiumConfirm, setShowPremiumConfirm] = useState(false);
  const eventsDropdownRef = useRef(null);

  // Close events dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (eventsDropdownRef.current && !eventsDropdownRef.current.contains(e.target)) {
        setEventsOpen(false);
      }
    };
    if (eventsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [eventsOpen]);

  const toggleEvent = (val) => {
    setFormData(prev => ({
      ...prev,
      event_dates: prev.event_dates.includes(val)
        ? prev.event_dates.filter(v => v !== val)
        : [...prev.event_dates, val]
    }));
  };

  const getBudgetNum = (range) => {
    const map = { 'under-2500': 2500, '2500-5000': 5000, '5000-10000': 10000, '10000-25000': 25000, '25000-plus': 50000 };
    return map[range] || 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.event_dates.length === 0) { toast.error('Please select at least one event date'); return; }
    if (!formData.budget_range) { toast.error('Please select a budget range'); return; }
    setLoading(true);

    try {
      const payload = {
        ...formData,
        phone: `${formData.phone_code} ${formData.phone}`,
        event_dates: formData.event_dates.map(v => EVENT_OPTIONS.find(o => o.value === v)?.label || v),
      };
      delete payload.phone_code;
      await axios.post(`${API}/sponsors/contact`, payload);

      // Store flag so redirect pages know form was submitted
      sessionStorage.setItem('wsp_sponsor_submitted', 'true');
      sessionStorage.setItem('wsp_sponsor_name', formData.name);
      sessionStorage.setItem('wsp_sponsor_company', formData.company);
      sessionStorage.setItem('wsp_sponsor_email', formData.email);

      toast.success('Thank you! Redirecting you now...');

      // Budget-based redirect
      const budgetVal = getBudgetNum(formData.budget_range);
      setTimeout(() => {
        if (budgetVal <= 5000) {
          navigate('/sponsors/community-packages');
        } else {
          setShowPremiumConfirm(true);
        }
      }, 1500);
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const selectedEventLabels = formData.event_dates.map(v => EVENT_OPTIONS.find(o => o.value === v)?.label || v);

  return (
    <section id="sponsors" className="py-24 sm:py-32 bg-[#050B14]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-['Bebas_Neue'] text-4xl sm:text-5xl tracking-tight uppercase text-white mb-4">
            Become a Partner
          </h2>
          <p className="text-lg text-[#A0B3C6] max-w-3xl mx-auto">
            Join us in celebrating the world's greatest sporting event. Connect your brand with passionate soccer fans across North America.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Heart & Stroke Foundation — Charity Partner · clean white card so the H&S brand pops */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-white border border-gray-200 rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-shadow"
            data-testid="heart-stroke-block"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="h-16 sm:h-20 flex items-center flex-shrink-0">
                <img
                  src="/partners/heart-stroke.jpg"
                  alt="Heart & Stroke Foundation"
                  className="h-full w-auto object-contain"
                  data-testid="heart-stroke-logo"
                  loading="lazy"
                />
              </div>
              <div className="min-w-0">
                <h3 className="font-['Bebas_Neue'] text-3xl uppercase text-gray-900 leading-tight">Charity Partner</h3>
                <p className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#E2231A]">In Support Of</p>
              </div>
            </div>

            <h4 className="text-2xl font-extrabold text-[#E2231A] mb-1">Heart &amp; Stroke Foundation</h4>
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-5">Promoting health · Saving lives · Enhancing recovery</p>

            <p className="text-gray-700 leading-relaxed mb-6">
              Heart &amp; Stroke is dedicated to fighting heart disease and stroke. With your support, we lead lifesaving research, drive system change, save lives, and improve the lives of every person in Canada.
            </p>

            <div className="space-y-2.5 mb-6">
              {[
                'Funding world-class heart & stroke research',
                'CPR & defibrillator training in every community',
                'Advocating for healthier policies across Canada',
              ].map(item => (
                <div key={item} className="flex items-start gap-2.5">
                  <Check size={18} weight="bold" className="text-[#E2231A] mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-700">{item}</p>
                </div>
              ))}
            </div>

            <a
              href="https://www.heartandstroke.ca"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#E2231A] hover:bg-[#B91C13] text-white px-6 py-3 font-bold uppercase tracking-wider transition-colors inline-block w-full text-center rounded-lg"
              data-testid="heart-stroke-link"
            >
              Learn More at heartandstroke.ca
            </a>
          </motion.div>

          {/* Sponsor Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-[#0E2E53] border border-[#00F0A9]/20 p-8 hover:border-[#00F0A9]/50 transition-colors neon-border"
            id="sponsor-form"
          >
            {/* (Msg 780) Public "Schedule Discovery Call" form removed —
                inquiries route to /partnerships. Admin books discovery calls
                internally via CRM. */}
            <h3 className="font-['Bebas_Neue'] text-3xl uppercase text-white mb-4">Partnership Inquiries</h3>
            <p className="text-[#A0B3C6] text-sm mb-6">Our team schedules discovery calls with vetted partners on invite-only basis. Introduce yourself via our Partner Hub and we'll circle back.</p>
            <a
              href="/partnerships"
              className="inline-flex items-center justify-center gap-2 bg-[#00F0A9] hover:bg-white text-[#0E2E53] font-black py-4 px-8 rounded uppercase tracking-wider transition-colors"
              data-testid="sponsor-partner-hub-cta"
            >
              Visit Partner Hub →
            </a>
            {false && (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="sponsor-contact-form">
              <div>
                <label className="flex items-center gap-2 text-sm uppercase tracking-wider text-[#A0B3C6] mb-2">
                  <User size={16} weight="bold" /> Full Name
                </label>
                <input type="text" required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full bg-[#050B14]/50 border border-[#00F0A9]/30 text-white px-4 py-3 focus:border-[#00F0A9] focus:ring-1 focus:ring-[#00F0A9] focus:outline-none transition-colors"
                  data-testid="sponsor-name-input" />
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm uppercase tracking-wider text-[#A0B3C6] mb-2">
                  <Envelope size={16} weight="bold" /> Email
                </label>
                <input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full bg-[#050B14]/50 border border-[#00F0A9]/30 text-white px-4 py-3 focus:border-[#00F0A9] focus:ring-1 focus:ring-[#00F0A9] focus:outline-none transition-colors"
                  data-testid="sponsor-email-input" />
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm uppercase tracking-wider text-[#A0B3C6] mb-2">
                  <Buildings size={16} weight="bold" /> Company <span className="text-[#A0B3C6]/50 normal-case text-xs">(optional)</span>
                </label>
                <input type="text" value={formData.company} onChange={(e) => setFormData({...formData, company: e.target.value})}
                  placeholder="Your company name"
                  className="w-full bg-[#050B14]/50 border border-[#00F0A9]/30 text-white px-4 py-3 focus:border-[#00F0A9] focus:ring-1 focus:ring-[#00F0A9] focus:outline-none transition-colors placeholder:text-[#A0B3C6]/40"
                  data-testid="sponsor-company-input" />
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm uppercase tracking-wider text-[#A0B3C6] mb-2">
                  <Phone size={16} weight="bold" /> Phone
                </label>
                <div className="flex gap-2">
                  <select value={formData.phone_code} onChange={(e) => setFormData({...formData, phone_code: e.target.value})}
                    className="bg-[#050B14]/50 border border-[#00F0A9]/30 text-white px-2 py-3 focus:border-[#00F0A9] focus:ring-1 focus:ring-[#00F0A9] focus:outline-none transition-colors w-[110px] flex-shrink-0 text-sm"
                    data-testid="sponsor-phone-code">
                    {COUNTRY_CODES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                  <input type="tel" required value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="Phone number"
                    className="w-full bg-[#050B14]/50 border border-[#00F0A9]/30 text-white px-4 py-3 focus:border-[#00F0A9] focus:ring-1 focus:ring-[#00F0A9] focus:outline-none transition-colors placeholder:text-[#A0B3C6]/40"
                    data-testid="sponsor-phone-input" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center gap-2 text-sm uppercase tracking-wider text-[#A0B3C6] mb-2">
                    <Calendar size={16} weight="bold" /> Preferred Date <span className="text-[#A0B3C6]/50 normal-case text-xs">(optional)</span>
                  </label>
                  <input type="date" max="2026-06-30" value={formData.preferred_date} onChange={(e) => setFormData({...formData, preferred_date: e.target.value})}
                    className="w-full bg-[#050B14]/50 border border-[#00F0A9]/30 text-white px-4 py-3 focus:border-[#00F0A9] focus:ring-1 focus:ring-[#00F0A9] focus:outline-none transition-colors"
                    data-testid="sponsor-date-input" />
                </div>
                <div>
                  <label className="flex items-center gap-2 text-sm uppercase tracking-wider text-[#A0B3C6] mb-2">
                    <Clock size={16} weight="bold" /> Preferred Time (EST)
                  </label>
                  <select required value={formData.preferred_time} onChange={(e) => setFormData({...formData, preferred_time: e.target.value})}
                    className="w-full bg-[#050B14]/50 border border-[#00F0A9]/30 text-white px-4 py-3 focus:border-[#00F0A9] focus:ring-1 focus:ring-[#00F0A9] focus:outline-none transition-colors"
                    data-testid="sponsor-time-input">
                    <option value="">Select time...</option>
                    {TIME_SLOTS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </div>

              {/* Event Dates Multi-Select */}
              <div>
                <label className="flex items-center gap-2 text-sm uppercase tracking-wider text-[#A0B3C6] mb-2">
                  <Calendar size={16} weight="bold" /> Event Dates
                </label>
                <div className="relative" ref={eventsDropdownRef} data-testid="event-dates-dropdown">
                  <button type="button" onClick={() => setEventsOpen(!eventsOpen)}
                    className="w-full bg-[#050B14]/50 border border-[#00F0A9]/30 text-white px-4 py-3 text-left flex items-center justify-between hover:border-[#00F0A9] transition-colors">
                    <span className={formData.event_dates.length ? 'text-white' : 'text-[#A0B3C6]/60'}>
                      {formData.event_dates.length ? `${formData.event_dates.length} event${formData.event_dates.length > 1 ? 's' : ''} selected` : 'Select events...'}
                    </span>
                    <CaretDown size={16} className={`text-[#00F0A9] transition-transform ${eventsOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {eventsOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-[#0E2E53] border border-[#00F0A9]/30 shadow-xl">
                      {EVENT_OPTIONS.map(opt => (
                        <label key={opt.value} className="flex items-center gap-3 px-4 py-3 hover:bg-[#00F0A9]/10 cursor-pointer border-b border-[#00F0A9]/10 last:border-0">
                          <input type="checkbox" checked={formData.event_dates.includes(opt.value)} onChange={() => toggleEvent(opt.value)}
                            className="w-4 h-4 rounded border-[#00F0A9]/50 text-[#00F0A9] focus:ring-[#00F0A9]" data-testid={`event-${opt.value}`} />
                          <span className="text-sm text-white">{opt.label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                {selectedEventLabels.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {selectedEventLabels.map(label => (
                      <span key={label} className="bg-[#00F0A9]/20 text-[#00F0A9] text-xs px-2 py-1 border border-[#00F0A9]/30">{label}</span>
                    ))}
                  </div>
                )}
              </div>

              {/* Budget Range — visible once events selected */}
              {formData.event_dates.length > 0 && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} transition={{ duration: 0.3 }}>
                  <label className="flex items-center gap-2 text-sm uppercase tracking-wider text-[#A0B3C6] mb-2">
                    <CurrencyDollar size={16} weight="bold" /> Budget Range
                  </label>
                  <select required value={formData.budget_range} onChange={(e) => setFormData({...formData, budget_range: e.target.value})}
                    className="w-full bg-[#050B14]/50 border border-[#00F0A9]/30 text-white px-4 py-3 focus:border-[#00F0A9] focus:ring-1 focus:ring-[#00F0A9] focus:outline-none transition-colors"
                    data-testid="sponsor-budget-input">
                    <option value="">Select budget range...</option>
                    {BUDGET_RANGES.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
                  </select>
                </motion.div>
              )}

              <div>
                <label className="flex items-center gap-2 text-sm uppercase tracking-wider text-[#A0B3C6] mb-2">
                  <ChatCircle size={16} weight="bold" /> Message (Optional)
                </label>
                <textarea value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} rows={3}
                  className="w-full bg-[#050B14]/50 border border-[#00F0A9]/30 text-white px-4 py-3 focus:border-[#00F0A9] focus:ring-1 focus:ring-[#00F0A9] focus:outline-none transition-colors resize-none"
                  data-testid="sponsor-message-input" />
              </div>

              <button type="submit" disabled={loading}
                className="w-full bg-[#00F0A9] text-[#050B14] px-8 py-4 font-bold uppercase tracking-wider hover:bg-[#00FFAF] hover:shadow-[0_0_20px_rgba(0,240,169,0.4)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="sponsor-submit-button">
                {loading ? 'Submitting...' : 'Request Sponsorship Package'}
              </button>
            </form>
            )}
          </motion.div>
        </div>
      </div>

      {/* Premium confirmation popup */}
      {showPremiumConfirm && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="premium-confirm-overlay">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-10 text-center"
          >
            <div className="w-16 h-16 bg-[#FF6B35]/10 rounded-full flex items-center justify-center mx-auto mb-5">
              <Check size={32} weight="bold" className="text-[#FF6B35]" />
            </div>
            <h3 className="font-['Bebas_Neue'] text-3xl uppercase text-gray-900 mb-3">Thank You!</h3>
            <p className="text-gray-600 text-base leading-relaxed mb-6">
              One of our amazing team members will reach out to discuss premium partnership options with you shortly!
            </p>
            <button
              onClick={() => { setShowPremiumConfirm(false); navigate('/meet-the-team'); }}
              className="bg-[#FF6B35] text-white px-8 py-3 rounded-full font-bold uppercase text-sm hover:bg-[#FF8C42] transition-all text-lg"
              data-testid="premium-confirm-close"
            >
              Meet the Team 😄
            </button>
          </motion.div>
        </div>
      )}
    </section>
  );
};
